import { Fragment, useEffect, useRef, useState } from "react";
import cn from "classnames";
import styles from "./Dropdown.module.scss";

export const Dropdown = ({
  label,
  disabled,
  openMenu,
  alertMessage,
  placeholder,
  defaultValue,
  options,
  size,
  formatOptionLabel,
  onChange,
  styleType = "primary",
  isSearchable = false,
  searchPlaceholder = "Type to filter…",
  isMulti = false,
  multiValueLabel = (count) => `${count} selected`,
}) => {
  const [localValue, setLocalValue] = useState(
    defaultValue ?? (isMulti ? [] : "")
  );
  const [menuIsOpened, setMenuIsOpened] = useState(openMenu || false);
  const [searchValue, setSearchValue] = useState("");

  const menuRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (menuIsOpened && menuRef.current && dropdownRef.current) {
      const modal = document.querySelector(".cabinet-modals");  // Change to your modal selector
      if (modal) {
        const modalRect = modal.getBoundingClientRect();
        const dropdownRect = dropdownRef.current.getBoundingClientRect();

        let availableHeight = modalRect.bottom - dropdownRect.top - 10;
        dropdownRef.current.style.maxHeight = `${availableHeight}px`;
      }
    }
  }, [menuIsOpened]);

  useEffect(() => {
    const listener = ({ target }) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!menuRef.current || menuRef.current.contains(target)) {
        return;
      }
      setMenuIsOpened(false);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [menuRef, menuIsOpened]);

  useEffect(() => {
    if (isMulti) {
      const normalized = Array.isArray(localValue) ? localValue : [];
      const allowed = new Set((options ?? []).map((o) => o.value));
      const cleaned = normalized.filter((v) => allowed.has(v));
      if (cleaned.length !== normalized.length) setLocalValue(cleaned);
      return;
    }

    const foundOption = options?.find((o) => o.value === localValue);
    if (localValue && !foundOption) {
      onChange(null);
      setLocalValue("");
    }
  }, [options, localValue, onChange])

  useEffect(() => {
    if (!menuIsOpened) setSearchValue("");
  }, [menuIsOpened]);

  const onChangeHandler = (value) => {
    if (isMulti) {
      const prev = Array.isArray(localValue) ? localValue : [];
      const next = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      setLocalValue(next);
      onChange?.(next);
      return;
    }

    onChange?.(value);
    setLocalValue(value);
  };

  const renderOptionsBlock = (isGrouped) => {
    const chosenItem = (value) => {
      if (isMulti) {
        const current = Array.isArray(localValue) ? localValue : [];
        return current.includes(value) ? (
          <div className={styles.chosen}>
            <img
              src={`${process.env.PUBLIC_URL}/icons/check.svg`}
              alt="selected"
            />
          </div>
        ) : null;
      }
      return value === localValue ? (
        <div className={styles.chosen}>
          <img
            src={`${process.env.PUBLIC_URL}/icons/check.svg`}
            alt="selected"
          />
        </div>
      ) : null;
    };

    const mappedItems = (item, index, itemsToMap) => {
      if (formatOptionLabel) {
        return formatOptionLabel(
          item.value,
          item.label,
          isGrouped ? item.group : undefined
        );
      }
      const showHR =
        isGrouped &&
        !!itemsToMap[index - 1] &&
        itemsToMap[index - 1].group !== itemsToMap[index].group;
      return (
        <Fragment key={`${item.label}_${item.value}`}>
          {showHR ? <hr /> : null}
          <div
            className={cn({ [styles.disabled]: item.disabled })}
            {...(!item.disabled && {
              onClick: () => onChangeHandler(item.value),
            })}
          >
            <span>{item.label}</span>
            {chosenItem(item.value)}
          </div>
        </Fragment>
      );
    };

    const filteredOptions =
      isSearchable && searchValue
        ? options?.filter((o) => {
            const q = searchValue.toLowerCase();
            const value = (o?.value ?? "").toString().toLowerCase();
            const label = (o?.label ?? "").toString().toLowerCase();
            return value.includes(q) || label.includes(q);
          })
        : options;

    const itemsToMap = isGrouped
      ? filteredOptions?.sort((a, b) => {
          if (a.group && b.group) return a.group.localeCompare(b.group);
          return 1;
        })
      : filteredOptions;

    return (
      <div className={styles.optionsBlock} ref={dropdownRef} key="options-block">
        {isSearchable && (
          <div
            className={styles.searchRow}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={searchPlaceholder}
              className={styles.searchInput}
            />
          </div>
        )}
        {itemsToMap?.map((item, index) => mappedItems(item, index, itemsToMap))}
      </div>
    );
  };

  const onMenuClick = () => {
    if (!disabled) setMenuIsOpened(!menuIsOpened);
  };

  const setTypographyLabel = () => {
    if (isMulti) {
      const current = Array.isArray(localValue) ? localValue : [];
      if (current.length === 0) return placeholder || "";
      if (current.length === 1) {
        const foundOption = options?.find((o) => o.value === current[0]);
        return foundOption?.label ?? current[0];
      }
      return multiValueLabel(current.length);
    }

    const foundOption = options?.find((o) => o.value === localValue);
    return (localValue && foundOption ? foundOption.label : "") || placeholder || "";
  };
  return (
    <div
      className={cn([styles[styleType]], {
        [styles.disabled]: disabled,
        [styles.shortWidth]: size === "short",
      })}
    >
      {label && <span>{label}</span>}
      <div
        role="menuitem"
        tabIndex={0}
        onClick={onMenuClick}
        className={styles.dropdownMenu}
        ref={menuRef}
      >
        <span
          key="dropdown-field"
          className={cn(styles.dropdownField, {
            [styles.placeholder]: !localValue,
            [styles.error]: alertMessage,
            [styles.disabled]: disabled,
            [styles.opened]: menuIsOpened,
          })}
        >
          <span>{setTypographyLabel()}</span>
        </span>
        <div
          key="dropdown-menu-icon"
          className={cn(styles.dropdownMenuIcon, {
            [styles.disabled]: disabled,
            [styles.withAlert]: alertMessage,
          })}
        >
          <img
            src={`${process.env.PUBLIC_URL}/icons/${
              menuIsOpened ? "chevron-down" : "chevron-down"
            }.svg`}
            className={styles.arrowIcon}
            alt="shevron"
          />
        </div>
        {menuIsOpened && renderOptionsBlock(options?.some((i) => !!i.group))}
        {alertMessage && !menuIsOpened && (
          <div className={styles.errorStyle}>
            <span>{alertMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};
